import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";
import { FaCalendarAlt, FaCheck, FaTimes } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "../context/LanguageContext";
import "../styles/common.css";
import "./AjouterEvenement.css";

export default function AjouterEvenement() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    animal: "",
    type_evenement: "",
    date: "",
    details: "",
  });

  const [animaux, setAnimaux] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    animal: "",
    type_evenement: "",
    date: "",
  });
  const [touchedFields, setTouchedFields] = useState({
    animal: false,
    type_evenement: false,
    date: false,
  });

  // Types d'événements à partir du modèle Django
  // Note: "naissance" et "achat" sont créés automatiquement lors de l'ajout d'un animal
  const TYPE_EVENEMENT_CHOICES = React.useMemo(() => [
    { value: "vendu", label: t.sale },
    { value: "mort", label: t.dead },
    { value: "zakat", label: t.zakat },
  ], [t]);

  useEffect(() => {
    const fetchAnimaux = async () => {
      try {
        const animauxRes = await api.get("/api/animaux/");
        setAnimaux(animauxRes.data);
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
      }
    };
    fetchAnimaux();
  }, []);

  const validateRequiredField = (name, value) => {
    if (!value || (typeof value === 'string' && value.trim() === "") || value === null) {
      return t.fieldRequired;
    }
    return "";
  };

  const validateField = (name, value) => {
    if (name === "animal" || name === "type_evenement" || name === "date") {
      return validateRequiredField(name, value);
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Validation en temps réel si le champ a été touché
    if (touchedFields[name] || fieldErrors[name]) {
      const error = validateField(name, value);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    // Marquer le champ comme touché
    setTouchedFields(prev => ({ ...prev, [name]: true }));
    
    // Valider le champ
    const error = validateField(name, value);
    setFieldErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Marquer tous les champs obligatoires comme touchés
    const newTouchedFields = {
      animal: true,
      type_evenement: true,
      date: true,
    };
    setTouchedFields(newTouchedFields);
    
    // Valider tous les champs obligatoires
    const newFieldErrors = {
      animal: validateField("animal", formData.animal),
      type_evenement: validateField("type_evenement", formData.type_evenement),
      date: validateField("date", formData.date),
    };
    setFieldErrors(newFieldErrors);
    
    // Vérifier s'il y a des erreurs
    const hasErrors = Object.values(newFieldErrors).some(error => error !== "");
    
    if (hasErrors) {
      return; // Ne pas soumettre si il y a des erreurs
    }
    
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setLoading(true);
    try {
      // Créer l'événement
      await api.post(`/api/evenements/`, formData);
      
      // Si l'événement est "vendu" ou "mort", mettre à jour le statut de l'animal
      if (formData.type_evenement === "vendu" || formData.type_evenement === "mort") {
        try {
          const animalData = {
            statut: formData.type_evenement === "vendu" ? "vendu" : "mort"
          };
          await api.patch(`/api/animaux/${formData.animal}/`, animalData);
        } catch (animalError) {
          console.error("Erreur lors de la mise à jour du statut de l'animal:", animalError);
          // Ne pas bloquer la création de l'événement si la mise à jour du statut échoue
          toast.warning("Événement créé mais erreur lors de la mise à jour du statut de l'animal.");
        }
      }
      
      setShowConfirmModal(false);
      toast.success("Événement ajouté avec succès !");
      setTimeout(() => {
        navigate("/evenements");
      }, 500);
    } catch (error) {
      console.error("Erreur lors de l'ajout:", error);
      
      // Helper function to extract string values from error data
      const extractErrorMessages = (data) => {
        const messages = [];
        
        // Check if data is a plain object
        if (data && typeof data === 'object' && !Array.isArray(data) && data !== null) {
          Object.values(data).forEach(value => {
            if (typeof value === 'string' && value.trim()) {
              messages.push(value.trim());
            } else if (typeof value === 'number') {
              messages.push(String(value));
            } else if (Array.isArray(value)) {
              // Flatten arrays and recursively extract messages
              value.forEach(item => {
                const extracted = extractErrorMessages(item);
                messages.push(...extracted);
              });
            } else if (value && typeof value === 'object') {
              // Recursively extract from nested objects
              const extracted = extractErrorMessages(value);
              messages.push(...extracted);
            }
          });
        } else if (Array.isArray(data)) {
          // Handle array at root level
          data.forEach(item => {
            const extracted = extractErrorMessages(item);
            messages.push(...extracted);
          });
        } else if (typeof data === 'string' && data.trim()) {
          messages.push(data.trim());
        } else if (typeof data === 'number') {
          messages.push(String(data));
        }
        
        return messages;
      };
      
      // Try to extract error message with fallback hierarchy
      let errorMessage = error.response?.data?.detail || 
                        error.response?.data?.message;
      
      if (!errorMessage && error.response?.data) {
        const extractedMessages = extractErrorMessages(error.response.data);
        if (extractedMessages.length > 0) {
          errorMessage = extractedMessages.join(", ");
        }
      }
      
      errorMessage = errorMessage || "Une erreur s'est produite lors de l'ajout.";
      toast.error(errorMessage);
      setShowConfirmModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  const isFormValid = () => {
    if (!formData.animal) return false;
    if (!formData.type_evenement || !formData.type_evenement.trim()) return false;
    if (!formData.date) return false;
    return true;
  };

  const getAnimalMatricule = () => {
    if (!formData.animal) return t.notSelected;
    const animalId = parseInt(formData.animal, 10);
    if (isNaN(animalId)) return t.notSelected;
    const animal = animaux.find(a => a.id === animalId);
    return animal ? animal.matricule : t.notSelected;
  };

  const getTypeEvenementLabel = () => {
    const choice = TYPE_EVENEMENT_CHOICES.find(c => c.value === formData.type_evenement);
    return choice ? choice.label : formData.type_evenement || t.notProvided;
  };

  return (
    <div className="ajouter-evenement-page">
      <div className="title-badge">
        <FaCalendarAlt className="page-title-icon" aria-hidden="true" />
        <span>{t.addEventTitle}</span>
      </div>

      <form onSubmit={handleSubmit} className="ajouter-evenement-form">
        {/* Bannière d'information */}
        <div className="form-info-banner">
          <span className="info-icon">ℹ️</span>
          <div className="info-content">
            <strong>{t.requiredFields}</strong>
            <span className="required-indicator">*</span> {t.requiredFieldNote}
          </div>
        </div>

        <div className="form-section-container">
          <div className="form">
            <label htmlFor="animal">
              Animal <span className="required-indicator">*</span>
            </label>
            <select
              id="animal"
              name="animal"
              value={formData.animal || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.animal && touchedFields.animal ? "input-error" : ""}
            >
              <option value="">-- Sélectionner un animal --</option>
              {animaux
                .filter(animal => animal.statut !== "vendu" && animal.statut !== "mort")
                .map((animal) => (
                  <option key={animal.id} value={animal.id}>
                    {animal.matricule}
                  </option>
                ))}
            </select>
            {fieldErrors.animal && touchedFields.animal && (
              <span className="field-error-message">{fieldErrors.animal}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="type_evenement">
              {t.eventTypeLabel} <span className="required-indicator">*</span>
            </label>
            <select
              id="type_evenement"
              name="type_evenement"
              value={formData.type_evenement || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.type_evenement && touchedFields.type_evenement ? "input-error" : ""}
            >
              <option value="">-- Sélectionner un type --</option>
              {TYPE_EVENEMENT_CHOICES.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
            {fieldErrors.type_evenement && touchedFields.type_evenement && (
              <span className="field-error-message">{fieldErrors.type_evenement}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="date">
              {t.eventDate} <span className="required-indicator">*</span>
            </label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.date && touchedFields.date ? "input-error" : ""}
            />
            {fieldErrors.date && touchedFields.date && (
              <span className="field-error-message">{fieldErrors.date}</span>
            )}
          </div>

          <div className="form full-width">
            <label htmlFor="details">{t.detailsLabel}</label>
            <textarea
              id="details"
              name="details"
              value={formData.details || ""}
              onChange={handleChange}
              placeholder={t.eventDetails}
              rows="3"
            ></textarea>
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="actions">
          <button
            type="submit"
            className="save-but"
            disabled={!isFormValid() || loading}
          >
            <FaCheck />
            <span>{loading ? t.registering : t.register}</span>
          </button>
          <button
            type="button"
            onClick={() => navigate("/evenements")}
            className="cancel-but"
            disabled={loading}
          >
            <FaTimes />
            <span>{t.cancel}</span>
          </button>
        </div>
      </form>

      {/* Modal de confirmation */}
      {showConfirmModal && (
        <div className="confirm-modal-overlay" onClick={handleCancelConfirm}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <FaCheck className="confirm-modal-icon" />
              <h3>{t.confirmAdd}</h3>
            </div>
            <div className="confirm-modal-body">
              <p className="confirm-intro">
                {t.verifyInfoBeforeAdd}
              </p>
              <div className="confirm-summary">
                <div className="summary-row">
                  <span className="summary-label">{t.animal}:</span>
                  <span className="summary-value">{getAnimalMatricule()}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">{t.summaryEventType}</span>
                  <span className="summary-value">{getTypeEvenementLabel()}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">{t.summaryDate}</span>
                  <span className="summary-value">{formData.date || t.notProvided}</span>
                </div>
                {formData.details && (
                  <div className="summary-row">
                    <span className="summary-label">{t.summaryDetails}</span>
                    <span className="summary-value">{formData.details}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="confirm-modal-footer">
              <button
                type="button"
                onClick={handleCancelConfirm}
                className="confirm-modal-cancel"
                disabled={loading}
              >
                <FaTimes />
                <span>{t.cancel}</span>
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                className="confirm-modal-confirm"
                disabled={loading}
              >
                <FaCheck />
                <span>{loading ? t.registering : t.confirm}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}
